import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { wordFrequency } from "../lib/frequency";

const SAMPLE = "The quick brown fox the lazy dog the fox runs the dog sleeps";

export function WordFrequencyTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [caseInsensitive, setCaseInsensitive] = useState(true);
  const [minLength, setMinLength] = useState(1);
  const [topN, setTopN] = useState(20);

  const result = useMemo(
    () => wordFrequency(input, { caseInsensitive, minLength, topN }),
    [input, caseInsensitive, minLength, topN],
  );
  const maxCount = result.items[0]?.count ?? 1;

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.wf_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.wf_placeholder()}
            className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 text-sm"
          />
        </div>
        <div className="grid items-end gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-min`}>{m.wf_minlen()}</Label>
            <Input
              id={`${uid}-min`}
              type="number"
              min={1}
              max={50}
              value={minLength}
              onChange={(e) =>
                setMinLength(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-top`}>{m.wf_topn()}</Label>
            <Input
              id={`${uid}-top`}
              type="number"
              min={1}
              max={500}
              value={topN}
              onChange={(e) =>
                setTopN(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={caseInsensitive}
              onChange={(e) => setCaseInsensitive(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary"
            />
            {m.wf_case()}
          </label>
        </div>
      </div>

      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.wf_results_label()}
          </h2>
          <div className="flex gap-2 text-xs font-medium">
            <span
              data-testid="wf-total"
              className="rounded-full bg-muted px-2.5 py-0.5 text-muted-foreground"
            >
              {m.wf_total({ n: String(result.total) })}
            </span>
            <span
              data-testid="wf-unique"
              className="rounded-full bg-muted px-2.5 py-0.5 text-muted-foreground"
            >
              {m.wf_unique({ n: String(result.unique) })}
            </span>
          </div>
        </div>
        {result.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{m.wf_empty()}</p>
        ) : (
          <ol data-testid="wf-list" className="space-y-2">
            {result.items.map((item) => (
              <li key={item.word} className="flex items-center gap-3 text-sm">
                <span className="w-28 shrink-0 truncate font-mono">
                  {item.word}
                </span>
                <div className="h-5 flex-1 overflow-hidden rounded bg-muted/40">
                  <div
                    className="h-full rounded bg-primary"
                    style={{ width: `${(item.count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">
                  {item.count}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
